import type { Router } from 'vue-router'

import { reportError } from './observability'

/**
 * Recovery from a chunk the server no longer has (#88).
 *
 * Every route component is a hashed chunk loaded on demand. A tab opened
 * before a deploy keeps asking for the old hashes, which the new deployment
 * does not serve, so the first navigation after a deploy breaks with "Unable
 * to preload CSS" or "Failed to fetch dynamically imported module". One reload
 * fetches the new index and cures it.
 *
 * The reload happens once per address, remembered in `sessionStorage` so a
 * chunk that is truly gone (a broken deploy, a CDN fault) is reported instead
 * of reloading forever. A navigation that succeeds forgets the markers, so
 * the next deploy gets its own single reload.
 */

const MARKER_PREFIX = 'chunk-reload:'

/** Vite dispatches a plain Event with the failure hung off `payload`. */
interface PreloadErrorEvent extends Event {
  payload: unknown
}

const CHUNK_FAILURE =
  /Failed to fetch dynamically imported module|Unable to preload CSS|Importing a module script failed/

export interface ChunkRecoveryOptions {
  /** Replaced in tests: jsdom's Location cannot be spied on. */
  reload?: () => void
}

function isChunkFailure(error: unknown): error is Error {
  return error instanceof Error && CHUNK_FAILURE.test(error.message)
}

/**
 * True when this is the first failure at this address and the marker is now
 * stored. Without storage the shell cannot remember that it reloaded, and a
 * reload on every failure is a loop, so that counts as "not the first".
 */
function markFirstFailure(): boolean {
  const key = `${MARKER_PREFIX}${window.location.href}`
  try {
    if (window.sessionStorage.getItem(key) !== null) return false
    window.sessionStorage.setItem(key, '1')
    return true
  } catch {
    return false
  }
}

function forgetMarkers(): void {
  try {
    const storage = window.sessionStorage
    const stale: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key !== null && key.startsWith(MARKER_PREFIX)) stale.push(key)
    }
    for (const key of stale) storage.removeItem(key)
  } catch {
    // No storage means no markers were ever stored, so there is nothing to forget.
  }
}

export function installChunkRecovery(router: Router, options: ChunkRecoveryOptions = {}): void {
  const reload = options.reload ?? (() => window.location.reload())

  function recover(error: unknown): void {
    if (markFirstFailure()) {
      reload()
      return
    }
    reportError(error)
  }

  window.addEventListener('vite:preloadError', (event) => {
    // Prevented, or Vite rethrows and the router reports the same failure twice.
    event.preventDefault()
    recover((event as PreloadErrorEvent).payload)
  })

  router.onError((error: unknown) => {
    if (isChunkFailure(error)) recover(error)
  })

  router.afterEach((_to, _from, failure) => {
    if (failure === undefined) forgetMarkers()
  })
}
